import { AfterViewInit, Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetDataPoint, DataPoint, ISensorData } from '@services/datapoints-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, interval, Subject, takeUntil } from 'rxjs';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { ChartComponent, AxisModel, SeriesModel } from '@syncfusion/ej2-angular-charts';
import { AssetsDataService } from '@services/assets-data.service';
import { convertEpochToDateString } from '@functions/functions';

/**
 * DataPointGraphDialogData
 */
export class DataPointGraphDialogData {
	datapoint: DataPoint;
	accountGUID: string;
	organisationUID: string;
}

@Component({
	selector: 'app-datapoint-graph',
	templateUrl: './datapoint-graph.component.html',
	styleUrls: ['./datapoint-graph.component.scss']
})
export class DatapointGraphComponent implements OnInit, AfterViewInit, OnDestroy {

	// ======================
	// OUTPUT EVENTS
	// ======================
	/** Emits when the chart image is updated */
	@Output() imageUpdated = new EventEmitter<any>();

	// ======================
	// PUBLIC VARIABLES
	// ======================
	/** Default chip durations for quick selection */
	public chipDurations: {
		time: string;
		text: string;
		toolTip: string;
		default?: boolean;
	}[] = [
			{ time: "00:00:30", text: "30m", toolTip: "30 Minutes" },
			{ time: "00:01:00", text: "1h", toolTip: "1 Hour", default: true },
			{ time: "00:02:00", text: "2h", toolTip: "2 Hours" },
			{ time: "00:04:00", text: "4h", toolTip: "4 Hours" },
			{ time: "00:08:00", text: "8h", toolTip: "8 Hours" },
			{ time: "00:12:00", text: "12h", toolTip: "12 Hours" }
		];

	/** Currently selected duration string */
	public strDuration: string = this.chipDurations.find(t => t.default == true)?.time ?? "";

	/** Dialog form reference */
	public dialogForm: FormGroup;

	/** Spinner flag */
	public spinner: boolean = false;

	/** All datapoints loaded */
	public dataPoints: AssetDataPoint[] = [];

	/** Parsed sensor data for charting */
	public parsedData: ISensorData[] = [];

	/** Raw datapoint values from API */
	public dataPointValues: object[] = [];

	/** Display minimum value */
	public dpMin: string = '';

	/** Display maximum value */
	public dpMax: string = '';

	/** Chart start date */
	public startDate: Date;

	/** Chart end date */
	public endDate: Date;

	/** Time Chard was last updated */
	public lastUpdatedTime: string;

	/** Currently visible start date in chart */
	public visibleStartDate: Date;

	/** Currently visible end date in chart */
	public visibleEndDate: Date;

	/** Chart width */
	public chartWidth: string = '700px';

	/** Chart height */
	public chartHeight: string = '350px';

	/** Chart zoom settings */
	public zoomSettings: object;

	/** X-axis configuration for the chart */
	public sensorXAxis: object;

	/** Chart legend configuration */
	public legend: object;

	/** Chart tooltip configuration */
	public tooltipSettings: object;

	/** Tooltip UI configuration */
	public tooltip: object = { placement: 'Before', isVisible: false, showOn: 'Focus' };

	/** Chart selection mode */
	public selectionMode: string;

	// ======================
	// PRIVATE VARIABLES
	// ======================
	/** Short month names for date formatting */
	private shortMonths: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

	/** Currently selected account GUID */
	private selectedAccountUID: string;

	/** Currently selected organisation UID */
	private selectedOrganisationUID: string;

	/** Subject to finalize all subscriptions */
	private finalise = new Subject<void>();

	/** datapoint */
	public datapoint: DataPoint;

	// ======================
	// VIEWCHILD
	// ======================
	/** Reference to the Syncfusion chart */
	@ViewChild('myChart') public myChart: ChartComponent;
	intervalObject: any;

	// ======================
	// CONSTRUCTOR
	// ======================
	/**
	 * DatapointGraphComponent constructor
	 * @param dialogRef Reference to the MatDialog
	 * @param globalFunctions Global utility functions
	 * @param siteSubscriptions Site subscription observables
	 * @param dialogData DataPoint object passed into dialog
	 * @param assetsDataService Service to fetch asset datapoint values
	 */
	constructor(
		public dialogRef: MatDialogRef<DatapointGraphComponent>,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: DataPointGraphDialogData,
		private assetsDataService: AssetsDataService
	) {
		this.datapoint = dialogData.datapoint;
	}

	// ======================
	// LIFECYCLE HOOKS
	// ======================
	/** OnInit lifecycle hook */
	ngOnInit(): void {
		this.setXAxis();
		this.subscribeToSiteChanges();
		this.startAutoRefresh(); // Only call once
	}

	/** AfterViewInit lifecycle hook */
	ngAfterViewInit(): void { }

	/** ngOnDestroy lifecycle hook */
	ngOnDestroy(): void {
		this.intervalObject = undefined;
		// Stop auto-refresh
		this.finalise.next();
		this.finalise.complete();
	}

	// ======================
	// SUBSCRIPTIONS
	// ======================
	/**
	 * Subscribe to selected account and organisation changes
	 */
	private subscribeToSiteChanges(): void {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		])
			.pipe(filter(([account, org]) => !!account && !!org))
			.subscribe({
				next: ([account, org]) => {
					this.selectedAccountUID = account;
					this.selectedOrganisationUID = org;

					this.zoomSettings = {
						mode: 'X',
						enablePinchZooming: true,
						enableSelectionZooming: true,
						enableScrollbar: true
					};
					this.legend = { visible: true };
					this.tooltipSettings = { enable: true };
					this.selectionMode = 'DragX';

					this.selectTime(this.strDuration);
				},
				error: error => this.globalFunctions.HandleError(error, 'Error: combined route parameters', false)
			});
	}

	// ======================
	// CHART FUNCTIONS
	// ======================
	/**
	 * Adjust chart width/height based on window
	 */
	setChartWidth(): void {
		this.chartWidth = `${window.innerWidth / 2.75}px`;
		this.chartHeight = `${window.innerHeight / 2.75}px`;
	}

	/**
	 * Handle chart zoom event
	 * @param event Zoom event object from Syncfusion chart
	 */
	onZoom(event: any): void {
		if (event.axis.orientation !== 'Vertical') {
			this.visibleStartDate = event.currentZoomFactor !== 1 ? new Date(event.currentVisibleRange.min) : this.startDate;
			this.visibleEndDate = event.currentZoomFactor !== 1 ? new Date(event.currentVisibleRange.max) : this.endDate;
		}
	}

	/**
	 * Handle chart scroll event
	 * @param event Scroll event object from Syncfusion chart
	 */
	onScroll(event: any): void {
		if (event.axis.orientation !== 'Vertical') {
			this.visibleStartDate = event.zoomFactor !== 1 ? new Date(event.range.min) : this.startDate;
			this.visibleEndDate = event.zoomFactor !== 1 ? new Date(event.range.max) : this.endDate;
		}
	}

	/**
	 * Format chart tooltip before rendering
	 * @param args Tooltip render arguments
	 */
	onTooltipRender(args: any): void {
		const timestamp: Date = args.data.pointX;
		const formattedDate = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp.getMinutes().toString().padStart(2, '0')}:${timestamp.getSeconds().toString().padStart(2, '0')} ${timestamp.getDate()} ${this.shortMonths[timestamp.getMonth()]}`;
		args.text = `${formattedDate} :${args.text.split(":").pop()}`;
	}

	// ======================
	// DURATION FUNCTIONS
	// ======================
	/**
	 * Set chart duration and update data
	 * @param duration Duration string in format "DD:HH:MM"
	 */
	selectTime(duration: string): void {
		this.strDuration = duration;
		this.startAutoRefresh();
		this.refreshChartData();
	}

	/** startAutoRefresh */
	private startAutoRefresh(): void {
		if (!this.intervalObject) {
			this.intervalObject = interval(30000)
				.pipe(takeUntil(this.finalise))
				.subscribe(() => {
					this.refreshChartData();
				});
		}
	}

	/** Calculate new time range and fetch data */
	private refreshChartData(): void {
		const [days, hours, minutes] = this.strDuration.split(':').map(Number);

		const newEndDate = new Date();
		const newStartDate = new Date();
		newStartDate.setDate(newEndDate.getDate() - days);
		newStartDate.setHours(newEndDate.getHours() - hours);
		newStartDate.setMinutes(newEndDate.getMinutes() - minutes);

		this.startDate = newStartDate;
		this.endDate = newEndDate;
		this.visibleStartDate = newStartDate;
		this.visibleEndDate = newEndDate;

		this.GetDataPointValues(this.datapoint, newStartDate.getTime(), newEndDate.getTime())
			.catch(err => console.error('refreshChartData failed', err));
	}

	/**
	 * Navigate forward/backward by the current duration
	 * @param isForward True to move forward, false to move backward
	 */
	navigateByDuration(isForward: boolean): void {

		if (isForward == false) {
			// Stop auto-refresh
			this.finalise.next();
			this.finalise.complete();
			this.intervalObject = undefined;
		}

		const durationMs = this.strDuration === 'custom' ? this.endDate.getTime() - this.startDate.getTime() : this.timeSpanToMilliseconds(this.strDuration);
		let start = isForward ? this.endDate.getTime() : this.startDate.getTime() - durationMs;
		let end = start + durationMs;

		if (isForward && end > Date.now()) {
			end = Date.now();
			start = end - durationMs;
		}

		this.startDate = new Date(start);
		this.endDate = new Date(end);

		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;

		this.GetDataPointValues(this.datapoint, start, end).then(() => {
			const maxTimestamp = Math.max(...this.parsedData.map(dp => dp.timestamp));
			// If latest data is within 30 minutes and interval hasn't started
			if ((Date.now() - maxTimestamp) < 30 * 60 * 1000 && !this.intervalObject) {
				this.startAutoRefresh();
			}
		});

	}

	/**
	 * Convert duration string "DD:HH:MM" to milliseconds
	 * @param timeSpan Duration string
	 * @returns Milliseconds equivalent
	 */
	timeSpanToMilliseconds(timeSpan: string): number {
		const [days, hours, minutes] = timeSpan.split(':').map(Number);
		if (isNaN(days) || isNaN(hours) || isNaN(minutes)) throw new Error('Invalid time span format');
		return (days * 24 * 60 * 60 * 1000) + (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
	}

	/**
	 * Set the X axis configuration for the chart
	 * @param lines Optional stripLines to add to X-axis
	 */
	setXAxis(lines: object[] = []): void {
		// const hours = parseInt(this.strDuration.split(':')[1]);
		this.sensorXAxis = {
			valueType: 'DateTime',
			intervalType: "Minutes", // 'Hours',
			labelFormat: 'HH:mm <br> dd-MMM-yy',
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false }
		};
	}

	/**
	 * Show or hide spinner
	 * @param show True to display spinner, false to hide
	 */
	displaySpinner(show: boolean): void {
		this.spinner = show;
	}

	// ======================
	// DATA FETCHING
	// ======================
	/**
	 * Fetch datapoint values from the server and update the chart
	 * @param datapoint Datapoint to fetch
	 * @param dateStart Start timestamp (ms)
	 * @param dateEnd End timestamp (ms)
	 */
	async GetDataPointValues(datapoint: DataPoint, dateStart: number, dateEnd: number): Promise<boolean> {
		const fetchDataSubject = new Subject<void>();
		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		if (this.myChart) {
			this.myChart.primaryXAxis.zoomFactor = 1;
			this.myChart.primaryXAxis.zoomPosition = 0;
			this.myChart.clearSeries();
		}

		this.displaySpinner(true);
		let maxTimestamp: number = 0;
		return new Promise<boolean>((resolve, reject) => {
			this.assetsDataService.getDataPointValues$(this.selectedAccountUID, this.selectedOrganisationUID,
				dateStart, dateEnd, datapoint.DataPointID.toString())
				.pipe(
					takeUntil(fetchDataSubject),
					distinctUntilChanged(),
					finalize(() => {
						fetchDataSubject.next();
						fetchDataSubject.complete();
						this.displaySpinner(false);
						if (scrollableDiv) {
							scrollableDiv.scrollLeft = scrollX;
							scrollableDiv.scrollTop = scrollY;
						}
					})
				)
				.subscribe({
					next: (dataPointValues: any[]) => {
						try {
							this.parsedData = dataPointValues.map(dp => ({
								AssetId: 0,
								timestamp: dp.Timestamp,
								sampleDate: new Date(dp.Timestamp),
								...dp.Values
							}));

							const uniqueDP = [{ Dp: datapoint.DataPointID, Si: datapoint.SI, Min: datapoint.EngMin, Max: datapoint.EngMax }];
							const uniqueSI = [{ Si: datapoint.SI, Primary: datapoint.PrimaryIndex }];

							uniqueDP.forEach(DP => {
								const values = this.parsedData.map(item => item[`[${DP.Dp}]`]).filter(v => typeof v === 'number') as number[];
								DP.Min = values.length ? Math.min(...values) : 0;
								DP.Max = values.length ? Math.max(...values) : 0;
							});

							this.dpMin = `${parseFloat(uniqueDP[0].Min.toFixed(4))}${uniqueDP[0].Si}`;
							this.dpMax = `${parseFloat(uniqueDP[0].Max.toFixed(4))}${uniqueDP[0].Si}`;

							const axes: AxisModel[] = uniqueSI.map(unit => {
								let { min, max, interval } = this.findMinMaxForSi(uniqueDP, unit.Si);

								/** This might have originally been for the lineRun signal */
								if (min === max) {
									if (min === 1) { min = 0; max = 1.5 }
									else if (min === 0) { max = 1; }
									else {
										const padding = Math.abs(min * 0.1) || 1;
										min -= padding;
										max += padding;
									}
								}

								return {
									name: `Axis${unit.Si}`,
									minimum: min,
									maximum: max,
									interval: interval,
									majorTickLines: { width: 0 },
									minorTickLines: { width: 0 },
									majorGridLines: { width: 0 },
									minorGridLines: { width: 0 },
									rowIndex: 1,
									lineStyle: { width: 1 },
									labelFormat: `{value} ${unit.Si}`,
									opposedPosition: !unit.Primary
								};
							});

							if (axes.length) {
								axes[0].majorGridLines = { width: 1 };
								axes[0].opposedPosition = false;
								this.myChart.primaryYAxis = axes[0];
								this.myChart.axes = axes.slice(1);
							}

							const series: SeriesModel[] = [{
								animation: { enable: false },
								emptyPointSettings: { mode: 'Drop' },
								type: 'Line',
								dataSource: this.parsedData,
								name: datapoint.DPTitle,
								xName: 'timestamp',
								yName: `[${datapoint.DataPointID}]`,
								yAxisName: `Axis${datapoint.SI}`,
								marker: { visible: false, width: 8, height: 8, shape: 'Triangle', isFilled: true }
							}];

							this.myChart.clearSeries();
							this.myChart.addSeries(series);

							resolve(true);
						} catch (err) {
							console.error('Error processing data point values', err);
							reject(false);
						}
					},
					error: error => {
						this.dataPointValues = [];
						this.globalFunctions.HandleError(error, 'Error fetching datapoint values', false);
						reject(false);
					},
					complete: () => {
						this.lastUpdatedTime = this.convertEpoch(Date.now());
					}
				});
		});
	}

	// ======================
	// HELPER FUNCTIONS
	// ======================

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * Calculate min, max and interval for an SI unit
	 * @param data Array of datapoints
	 * @param targetSi Unit string
	 * @returns Object with min, max, interval
	 */
	findMinMaxForSi(data: any[], targetSi: string): { min: number, max: number, interval: number } {
		const filtered = data.filter(d => d.Si.toLowerCase() === targetSi.toLowerCase());
		if (!filtered.length) return { min: 0, max: 0, interval: 1 };

		const min = Math.floor(Math.min(...filtered.map(d => d.Min)));
		let max = Number(Math.max(...filtered.map(d => d.Max)).toFixed(1));

		let interval = (max - min) / 13;
		interval = interval > 1 ? Math.ceil(interval) : this.roundToNearestTenth(interval);
		max += interval;
		interval = min === max ? 1 : interval;

		return { min, max, interval };
	}

	/**
	 * Round a number to nearest tenth
	 * @param num Number to round
	 * @returns Rounded number
	 */
	roundToNearestTenth(num: number): number {
		return Math.ceil(num * 10) / 10;
	}

	/**
	 * Format a date into short string
	 * @param date Date object
	 * @returns Formatted string e.g., "23 Feb 14:25"
	 */
	getShortDate(date: Date): string {
		return `${date.getDate()} ${this.shortMonths[date.getMonth()]} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
	}

	/**
	 * Close the dialog
	 */
	async onCloseClick(): Promise<void> {

		// Stop auto-refresh
		this.finalise.next();
		this.finalise.complete();

		this.dialogRef.close();
	}
}